/**
 =========================================================
 * Material Dashboard 2 PRO React - v2.1.0
 =========================================================

 * Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
 * Copyright 2022 Creative Tim (https://www.creative-tim.com)

 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */
// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React examples
import PageLayout from "examples/LayoutContainers/PageLayout";

// Material Dashboard 2 PRO React context
import { useMaterialUIController } from "context";

function IllustrationLayout({
                                header = "",
                                title = "",
                                description = "",
                                illustration = "",
                                children,
                                logo = "",
                            }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const isVideo =
        typeof illustration === "string" &&
        (illustration.endsWith(".mp4") ||
            illustration.endsWith(".m4v") ||
            illustration.endsWith(".webm") ||
            illustration.includes(".mp4") ||
            illustration.includes(".m4v") ||
            illustration.includes(".webm"));

    return (
        <PageLayout background="white">
            <MDBox
                sx={{
                    position: "relative",
                    width: "100%",
                    minHeight: "100vh",
                    overflow: "hidden",
                    backgroundColor: darkMode ? "#111827" : "#ffffff",
                }}
            >
                <MDBox
                    sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        zIndex: 0,
                    }}
                >
                    {isVideo ? (
                        <video
                            autoPlay
                            muted
                            loop
                            playsInline
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                            }}
                        >
                            <source src={illustration} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    ) : (
                        <MDBox
                            sx={{
                                width: "100%",
                                height: "100%",
                                backgroundImage: `url(${illustration})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                backgroundRepeat: "no-repeat",
                            }}
                        />
                    )}
                </MDBox>

                <MDBox
                    sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 1,
                        background:
                            "linear-gradient(135deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.25) 100%)",
                    }}
                />

                <MDBox
                    sx={{
                        position: "relative",
                        zIndex: 2,
                        minHeight: "100vh",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 2,
                        py: 4,
                    }}
                >
                    <MDBox
                        sx={{
                            width: "100%",
                            maxWidth: { xs: "94%", sm: "540px", md: "580px" },
                            backgroundColor: "#ffffff",
                            borderRadius: "20px",
                            boxShadow: "0 20px 60px rgba(0,0,0,0.20)",
                            px: { xs: 3, sm: 5 },
                            py: { xs: 4, sm: 5 },
                        }}
                    >
                        <MDBox textAlign="center" mb={3}>
                            {logo && (
                                <MDBox mb={2} textAlign="center">
                                    <img
                                        src={logo}
                                        alt="Logo"
                                        style={{
                                            maxWidth: "100%",
                                            maxHeight: "140px",
                                            objectFit: "contain",
                                        }}
                                    />
                                </MDBox>
                            )}

                            {!header ? (
                                <>
                                    <MDBox mb={1}>
                                        <MDTypography variant="h4" fontWeight="bold">
                                            {title}
                                        </MDTypography>
                                    </MDBox>

                                    <MDTypography
                                        variant="body2"
                                        color="text"
                                        sx={{
                                            maxWidth: "420px",
                                            mx: "auto",
                                        }}
                                    >
                                        {description}
                                    </MDTypography>
                                </>
                            ) : (
                                header
                            )}
                        </MDBox>

                        <MDBox
                            sx={{
                                mt: 1,
                            }}
                        >
                            {children}
                        </MDBox>
                    </MDBox>
                </MDBox>
            </MDBox>
        </PageLayout>
    );
}

// Typechecking props for the IllustrationLayout
IllustrationLayout.propTypes = {
    header: PropTypes.node,
    title: PropTypes.string,
    description: PropTypes.string,
    children: PropTypes.node.isRequired,
    illustration: PropTypes.string,
    logo: PropTypes.string,
};

export default IllustrationLayout;